/**
 * cameraInteraction — is the USER dragging the camera right now?
 *
 * Head Follow, Eye Follow and the Attention node behave differently while
 * someone is tumbling or panning the camera with the mouse than when the
 * camera moves any other way (scripted moves, camera nodes, momentum, framing):
 *
 *   • their Dead Zone applies only at the START of a user drag — once the
 *     camera has moved past it, tracking is continuous for the rest of that
 *     drag (see grabDeadzone.js) — and never to any other camera motion;
 *   • Attention treats a drag as one gesture: one "notice", calmer tracking
 *     while held, and the settle blink / lose-interest timer from the release.
 *
 * "Dragging" = any mouse button held down on the 3D viewport canvas (orbit and
 * pan alike, edit and live camera modes alike — it doesn't need to know which
 * camera code is listening). Each new press gets a fresh `grabId`, so consumers
 * can tell one drag from the next without missing a quick release-and-regrab
 * between two frames.
 *
 * The renderer that owns the viewport calls `attach(gl.domElement)` (cheap and
 * idempotent — safe every frame). No THREE import; plain data + DOM listeners.
 */

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

export const cameraInteraction = {
  held: false,        // a button is down on the viewport
  grabId: 0,          // increments on every new press
  pressedAt: -Infinity,
  releasedAt: -Infinity,
  _el: null,

  /** Start listening on the viewport canvas. Re-attaches if the canvas changed. */
  attach(el) {
    if (!el || el === this._el) return
    this.detach()
    this._el = el
    el.addEventListener('pointerdown', onDown, true)
    if (typeof window !== 'undefined') {
      window.addEventListener('pointerup', onUp, true)
      window.addEventListener('pointercancel', onUp, true)
      window.addEventListener('blur', onBlur)
    }
  },

  detach() {
    if (this._el) this._el.removeEventListener('pointerdown', onDown, true)
    if (typeof window !== 'undefined') {
      window.removeEventListener('pointerup', onUp, true)
      window.removeEventListener('pointercancel', onUp, true)
      window.removeEventListener('blur', onBlur)
    }
    this._el = null
    this.held = false
  },

  /** Seconds since the last release (Infinity before the first one). */
  releasedAgo(t = now()) {
    return (t - this.releasedAt) / 1000
  },

  // Test seam: drive the state without real pointer events.
  _press(t = now()) { onDown({ timeStamp: t }, t) },
  _release(t = now()) { onUp({ buttons: 0 }, t) },
}

function onDown(_e, t = now()) {
  if (!cameraInteraction.held) {
    cameraInteraction.held = true
    cameraInteraction.grabId++
    cameraInteraction.pressedAt = t
  }
}

function onUp(e, t = now()) {
  // Only once EVERY button is up — releasing the right button while the left
  // is still held is still one drag.
  if (!cameraInteraction.held || (e && e.buttons)) return
  cameraInteraction.held = false
  cameraInteraction.releasedAt = t
}

// A drag can't survive the window losing focus (alt-tab mid-drag never sends
// the pointerup here).
function onBlur() { onUp({ buttons: 0 }) }

if (typeof window !== 'undefined') {
  window.__CAMERA_INTERACTION__ = cameraInteraction
}
